import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useContent } from '../../hooks/useContent';

const services = [
  { icon: '🏠', title: 'Residential Vastu', desc: 'Apartments, villas, farmhouses, bungalows, plots & renovation Vastu.', path: '/services/residential', color: '#E8871A', rgb: '232,135,26' },
  { icon: '🏢', title: 'Commercial Vastu', desc: 'Offices, shops, showrooms, restaurants, hotels, salons & clinics.', path: '/services/commercial', color: '#F0A500', rgb: '240,165,0' },
  { icon: '🏭', title: 'Industrial Vastu', desc: 'Factories, warehouses, industrial plots & manufacturing plants.', path: '/services/industrial', color: '#E8871A', rgb: '232,135,26' },
  { icon: '💼', title: 'Professional Spaces', desc: "Doctors' clinics, CA offices, law firms, coaching institutes & consultancies.", path: '/services', color: '#C9547A', rgb: '201,84,122' },
  { icon: '✨', title: 'Special Consultations', desc: 'Plot selection, property evaluation, new construction & business growth Vastu.', path: '/services', color: '#F0A500', rgb: '240,165,0' },
  { icon: '🔍', title: 'Vastu Audit & Energy Analysis', desc: 'Comprehensive energy analysis and Vastu audit for existing spaces.', path: '/contact', color: '#C9547A', rgb: '201,84,122' },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } }
};

const cardVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6 } }
};

const headingVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } }
};

export default function ServicesGrid() {
  const { content } = useContent();
  const s = content?.services;
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const list = s?.list ?? services;

  return (
    <section ref={sectionRef} style={{ padding: '100px 0', background: '#120A1E', position: 'relative', overflow: 'hidden' }}>
      {/* Animated background orbs */}
      <motion.div
        animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', top: '10%', left: '-5%', width: 400, height: 400,
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(232,135,26,0.06) 0%, transparent 70%)',
          pointerEvents: 'none'
        }}
      />
      <motion.div
        animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        style={{
          position: 'absolute', bottom: '10%', right: '-5%', width: 350, height: 350,
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,84,122,0.06) 0%, transparent 70%)',
          pointerEvents: 'none'
        }}
      />

      <div className="container" style={{ position: 'relative' }}>
        <motion.div
          variants={headingVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          style={{ textAlign: 'center', marginBottom: 60 }}
        >
          <p className="section-eyebrow">{s?.eyebrow ?? 'Areas We Serve'}</p>
          <h2 className="section-title">{s?.title ?? 'Our Consultancy Services'}</h2>
          <motion.div
            initial={{ width: 0 }}
            animate={isInView ? { width: 60 } : { width: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{ height: 2, background: 'linear-gradient(90deg, #E8871A, transparent)', margin: '20px auto' }}
          />
          <p style={{ opacity: 0.75, maxWidth: 560, margin: '0 auto', lineHeight: 1.8, color: '#FDF6EC' }}>
            {s?.subtitle}
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="home-services-grid"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}
        >
          {list.map((s: any, i: number) => (
            <motion.div key={i} variants={cardVariants}>
              <ServiceCard {...s} />
            </motion.div>
          ))}
        </motion.div>
      </div>
      <style>{`
        @media (max-width: 768px) {
          .home-services-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

function ServiceCard({ icon, title, desc, path, color, rgb }: typeof services[0]) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <Link to={path} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      <motion.div
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        whileHover={{ y: -8, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        style={{
          background: hovered ? `rgba(${rgb},0.1)` : 'rgba(255,255,255,0.04)',
          border: `1px solid ${hovered ? `rgba(${rgb},0.5)` : 'rgba(255,255,255,0.1)'}`,
          borderRadius: 12, padding: '36px 28px',
          cursor: 'pointer', height: '100%',
          boxShadow: hovered ? `0 20px 40px rgba(${rgb},0.15)` : 'none',
          transition: 'background 0.3s, border 0.3s, box-shadow 0.3s'
        }}
      >
        <motion.div
          animate={hovered ? { scale: [1, 1.15, 1], rotate: [0, 5, -5, 0] } : {}}
          transition={{ duration: 0.5 }}
          style={{
            width: 56, height: 56, borderRadius: 12,
            background: `rgba(${rgb},0.15)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, marginBottom: 20,
            border: `1px solid rgba(${rgb},0.3)`,
            boxShadow: hovered ? `0 0 20px rgba(${rgb},0.3)` : 'none',
            transition: 'box-shadow 0.3s'
          }}
        >
          {icon}
        </motion.div>

        <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, marginBottom: 12, color: '#FDF6EC' }}>
          {title}
        </h3>
        <p style={{ fontSize: 14, lineHeight: 1.7, color: '#FDF6EC', opacity: 0.75 }}>{desc}</p>

        <motion.p
          animate={hovered ? { x: 6 } : { x: 0 }}
          transition={{ type: 'spring', stiffness: 400 }}
          style={{ marginTop: 20, fontSize: 13, color, fontWeight: 600 }}
        >
          Learn more →
        </motion.p>
      </motion.div>
    </Link>
  );
}
